import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { queryClient } from '@/queryclient';
import { Job, JobStatus } from '@/types';
import { useMutation } from '@/utils/api';

export const ExportFormat = {
  PDF: 'pdf',
  ZIP: 'zip',
} as const;

export type ExportFormatType = (typeof ExportFormat)[keyof typeof ExportFormat];

// Constants for polling behavior (mirrors useAsyncExports)
const INITIAL_POLL_INTERVAL = 5000; // 5 seconds
const MAX_RETRIES = 60; // 5 minutes total (60 * 5s = 300s)
const MAX_POLL_INTERVAL = 30000; // 30 seconds maximum

export interface ReportConfig {
  title: string;
  client_name: string;
  sow: string;
  confidential_label: string;
  target: string;
  start_date: string;
  end_date: string;
  report_date: string;
  draft: boolean;
  retest: boolean;
  version: string;
  footer: string;
}

export interface ValidateReportParams {
  risk_keys?: string[];
  status_filter: string[];
  executive_summary_path?: string;
  narratives_path?: string;
  group_by?: string;
}

export interface ValidateReportError {
  type: string;
  risk_name: string;
  risk_key: string;
}

export interface ValidateReportResult {
  valid: boolean;
  errors: ValidateReportError[];
}

export interface ExportReportParams {
  risk_keys?: string[];
  status_filter: string[];
  config: ReportConfig;
  executive_summary_path?: string;
  narratives_path?: string;
  appendix_path?: string;
  export_format?: ExportFormatType;
  group_by?: string;
}

export interface LOAConfig {
  client_name: string;
  assessment_name: string;
  issue_date: string;
  undersigned_name: string;
  undersigned_designation: string;
  undersigned_email: string;
  licensing_line: string;
  content: string;
  draft: boolean;
}

export interface ExportLOAParams {
  risk_keys?: string[];
  status_filter: string[];
  config: LOAConfig;
}

interface ExportToastLabels {
  loading: string;
  success: string;
  download: string;
}

/**
 * Starts an export job, polls /my for completion, resolves the output file URL,
 * and surfaces progress/result toasts. Shared by report and LOA exports.
 */
async function runExportJob(
  axios: ReturnType<typeof useAxios>,
  endpoint: string,
  params: unknown,
  labels: ExportToastLabels
): Promise<Job> {
  const toastId = toast.loading(labels.loading, { duration: Infinity });

  try {
    const exportResponse = await axios.post<Job>(endpoint, params);
    if (!exportResponse?.data?.key) {
      throw new Error('Invalid export response');
    }

    let retryCount = 0;
    let completedJob: Job | undefined;

    while (retryCount < MAX_RETRIES) {
      const jobResponse = await axios.get('/my', {
        params: { key: exportResponse.data.key },
      });
      if (!jobResponse?.data?.jobs?.[0]) {
        throw new Error('Invalid job response');
      }

      const job = jobResponse.data.jobs[0];
      const status = job.status.slice(0, 2);

      if (status === JobStatus.Pass) {
        queryClient.invalidateQueries({ queryKey: getQueryKey.getMy('file') });
        const config = job.config as Record<string, string> | undefined;
        const outputPath = config?.output ?? job.dns;
        const fileRes = await axios.get<{ url: string }>('/file', {
          params: { name: outputPath },
        });
        (job as Job & { _pdfUrl?: string })._pdfUrl = fileRes.data.url;
        completedJob = job;
        break;
      }

      if (status === JobStatus.Fail) {
        throw new Error(job.message || 'Generation failed');
      }

      if (status !== JobStatus.Running && status !== JobStatus.Queued) {
        throw new Error(`Unknown job status: ${status}`);
      }

      const interval = Math.min(
        INITIAL_POLL_INTERVAL * (1 + Math.floor(retryCount / 2)),
        MAX_POLL_INTERVAL
      );
      await new Promise(resolve => setTimeout(resolve, interval));
      retryCount++;
    }

    if (!completedJob) {
      throw new Error(
        'Generation timed out after 5 minutes. Please try again.'
      );
    }

    const pdfUrl = (completedJob as Job & { _pdfUrl?: string })._pdfUrl;

    // Automatically trigger a browser download to the user's Downloads folder.
    // The success toast below keeps its 'Download' action so the user can
    // re-download manually. We use an anchor (not window.open) so this doesn't
    // open a new tab. Cross-origin caveat: when the URL is an S3 presigned URL
    // on a different origin, the browser may ignore the `download` attribute and
    // navigate instead; the manual toast action remains the reliable fallback.
    if (pdfUrl && typeof document !== 'undefined') {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    toast.dismiss(toastId);
    toast(labels.success, {
      duration: 15000,
      action: pdfUrl
        ? {
            label: labels.download,
            onClick: () => window.open(pdfUrl, '_blank', 'noopener,noreferrer'),
          }
        : undefined,
      actionButtonStyle: {
        background: 'rgb(var(--chariot-brand-primary))',
        color: 'white',
        border: 'none',
      },
    });
    return completedJob;
  } catch (error) {
    toast.dismiss(toastId);
    toast.error(error instanceof Error ? error.message : labels.loading, {
      duration: 8000,
    });
    throw error;
  }
}

/**
 * Hook to validate a PDF report before generation.
 * POSTs to /validate-report and returns any errors (missing definitions, narratives).
 */
export function useValidateReport() {
  const axios = useAxios();

  return useMutation<ValidateReportResult, Error, ValidateReportParams>({
    defaultErrorMessage: 'Failed to validate report',
    mutationFn: async (params: ValidateReportParams) => {
      const response = await axios.post<ValidateReportResult>(
        '/validate-report',
        params
      );
      return response.data;
    },
  });
}

/**
 * Hook to generate a PDF/ZIP report as an async job, then trigger download.
 */
export function useExportReport() {
  const axios = useAxios();

  return useMutation<Job, Error, ExportReportParams>({
    defaultErrorMessage: 'Failed to generate report',
    mutationFn: async (params: ExportReportParams) => {
      const formatLabel =
        params.export_format === ExportFormat.ZIP ? 'ZIP' : 'PDF';
      return runExportJob(axios, '/export/report', params, {
        loading: `Generating ${formatLabel} report...`,
        success: `${formatLabel} report generated`,
        download: formatLabel === 'ZIP' ? 'Download ZIP' : 'Download PDF',
      });
    },
  });
}

/**
 * Hook to generate a standalone Letter of Attestation as an async job, then
 * trigger download. POSTs to /export/loa.
 */
export function useExportLOA() {
  const axios = useAxios();

  return useMutation<Job, Error, ExportLOAParams>({
    defaultErrorMessage: 'Failed to generate letter of attestation',
    mutationFn: async (params: ExportLOAParams) =>
      runExportJob(axios, '/export/loa', params, {
        loading: 'Generating Letter of Attestation...',
        success: 'Letter of Attestation generated',
        download: 'Download LOA',
      }),
  });
}
